/**
 * ResetPasswordPage - Set New Password
 *
 * Handles the password reset callback from Better Auth.
 * The URL contains a token parameter that validates the reset request.
 * User enters a new password, which is submitted to Better Auth's reset endpoint.
 */

import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HiArrowLeft, HiCheckCircle, HiExclamationTriangle } from 'react-icons/hi2';
import { Input } from '../../components/ui/Input';
import { betterAuthResetPassword } from '../../services/auth';
import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from '../../services/auth-constants';
import { logger } from '../../../utils/logger';
import voicebrainLogo from '../../../assets/images/voicebrain-logo.svg';
import loginBackground from '../../../assets/images/login-background.jpg';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'VoiceBrain';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // No token = invalid link
  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center">
            <div className="w-16 h-16 bg-vb-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiExclamationTriangle className="w-8 h-8 text-vb-danger" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Invalid reset link
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link
              to="/v2/forgot-password"
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Request new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div
            className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="w-16 h-16 bg-vb-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiCheckCircle className="w-8 h-8 text-vb-success" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Password reset
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              Your password has been changed. You can now sign in with your new password.
            </p>
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (password.length > MAX_PASSWORD_LENGTH) {
      setError(`Password must not exceed ${MAX_PASSWORD_LENGTH} characters`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await betterAuthResetPassword(password, token);
      if (result.error) {
        setError(result.error);
        setIsLoading(false);
        return;
      }
      setSuccess(true);
      logger.info('[ResetPasswordPage] Password reset successful');
    } catch (err) {
      logger.error('[ResetPasswordPage] Error:', err);
      setError('Failed to reset password. The link may have expired.');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
      role="main"
      aria-label={`${BRAND_NAME} password reset`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />

      <div className="w-full max-w-sm relative z-10">
        <div
          className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8"
          role="form"
          aria-labelledby="reset-heading"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src={voicebrainLogo}
              alt={BRAND_NAME}
              className="h-10 mx-auto mb-3 invert dark:invert-0"
            />
            <h1
              id="reset-heading"
              className="text-lg font-semibold text-vb-text-primary dark:text-vb-text-primary-dark"
            >
              Choose a new password
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mt-1">
              Enter your new password below
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 p-3 rounded-lg bg-vb-danger/10 border border-vb-danger text-sm text-vb-danger"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Reset password form">
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
              >
                New password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                autoFocus
                aria-required="true"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
              >
                Confirm password
              </label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                aria-required="true"
                placeholder="Repeat your password"
              />
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !password || !confirmPassword}
              aria-busy={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset password'}
            </button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark hover:text-vb-primary transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-vb-text-tertiary/50 dark:text-vb-text-tertiary-dark/50">
          &copy; {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
